import { LocalDate } from '@js-joda/core';
import { Trans } from '@lingui/react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { invalidate } from '@react-three/fiber';
import { FormikActions } from 'components/Form';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import useForm from 'hooks/useForm';
import { i18n } from 'i18n';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import { enqueueSnackbar } from 'notistack';
import { SpoolStatus, useBulkUpdateSpoolsMutation } from 'providers/api';
import { formatUserFacingApiErrorMessage, getKnownApiErrorMessage } from 'providers/api/apiErrorMessages';
import { Permission } from 'providers/authorisation';
import React from 'react';
import { dayJsToLocalDate, LocalDateToDayJs, sanitiseNumber } from 'utils';
import {
  boolean,
  mixed,
  number,
  object,
  string
} from 'yup';
import { Model } from '../hooks/useModelState';
import PCNode from '../MineModel/Nodes/PCNode';
import PCSpool from '../MineModel/Spools/PCSpool';
import { spoolEndsLabel } from '../MineModel/Spools/utils';
import PipeTypeSelectField from '../NodeForm/PipeTypeSelectField';
import {
  lengthUnitLabel,
  metresToDisplay,
  SPOOL_NAME_MAX_LENGTH,
  SPOOL_OTHER_MAX_LENGTH,
  SPOOL_TAG_MAX_LENGTH,
  SpoolFormValues,
  spoolStraightLength,
  toSpoolFormValues,
  toSpoolPatch,
  toSpoolUpdateForm
} from './spoolFormUtils';

// A stored value already over the limit (seeded or imported) must not block saving the other fields.
const maxUnlessUnchanged = (max: number, initial: string, message: string) => string().test(
  'max-unless-unchanged',
  message,
  (value) => value === initial || (value ?? '').length <= max,
);

// Untyped: SchemaOf expects an object schema for LocalDate fields.
const createSchema = (initialValues: SpoolFormValues) => object().shape({
  name: maxUnlessUnchanged(SPOOL_NAME_MAX_LENGTH, initialValues.name, i18n._('Name must be at most {max} characters', { max: SPOOL_NAME_MAX_LENGTH })),
  pipeId: string().required(i18n._('Pipe Type is required')),
  allowFlowAB: boolean().required(),
  allowFlowBA: boolean().required().test(
    'at-least-one-direction',
    i18n._('At least one flow direction must be allowed'),
    (value, context) => value === true || (context.parent as SpoolFormValues).allowFlowAB === true,
  ),
  chokeLength: number()
    .min(0, i18n._('Loop length must be greater than or equal to 0'))
    .required(i18n._('Loop length is required')),
  status: mixed<SpoolStatus>().oneOf([SpoolStatus.Active, SpoolStatus.Inactive]).required(),
  installedOn: mixed<LocalDate>().nullable(),
  removedOn: mixed<LocalDate>().nullable().test(
    'removed-not-before-installed',
    i18n._('Removed date must be on or after the installed date'),
    (value, context) => {
      const { installedOn } = context.parent as SpoolFormValues;
      return !value || !installedOn || !value.isBefore(installedOn);
    },
  ),
  other: maxUnlessUnchanged(SPOOL_OTHER_MAX_LENGTH, initialValues.other, i18n._('Other must be at most {max} characters', { max: SPOOL_OTHER_MAX_LENGTH })),
  level: maxUnlessUnchanged(SPOOL_TAG_MAX_LENGTH, initialValues.level, i18n._('Level must be at most {max} characters', { max: SPOOL_TAG_MAX_LENGTH })),
  area: maxUnlessUnchanged(SPOOL_TAG_MAX_LENGTH, initialValues.area, i18n._('Area must be at most {max} characters', { max: SPOOL_TAG_MAX_LENGTH })),
});

const nodeLabel = (node: PCNode) => node.nodeName || node.id;

interface SpoolFormProps {
  spool: PCSpool;
  model: Model;
  onComplete: () => void;
}

const SpoolForm = ({ spool, model, onComplete }: SpoolFormProps) => {
  const unitSystem = model.project.unitSystemPreference;
  const unit = lengthUnitLabel(unitSystem);
  const labelA = nodeLabel(spool.pointA);
  const labelB = nodeLabel(spool.pointB);
  const mutation = useBulkUpdateSpoolsMutation(model.project.entityId);
  const initialValues = React.useMemo(() => toSpoolFormValues(spool, unitSystem), [spool, unitSystem]);

  const { formik, helpers } = useForm({
    mutation,
    formikConfig: {
      initialValues,
      validationSchema: createSchema(initialValues),
      onSubmit: async (values: SpoolFormValues) => {
        const update = toSpoolUpdateForm(spool.spoolId, initialValues, values, unitSystem);
        if (!update) {
          onComplete();
          return;
        }

        try {
          await mutation.mutateAsync({ projectId: model.project.entityId, spools: [update] });
        } catch (error) {
          const known = getKnownApiErrorMessage(error);
          if (known) {
            enqueueSnackbar(formatUserFacingApiErrorMessage(known), { variant: 'error' });
          }
          return;
        }

        // The scene's spool is patched in place, as the pipe and flow-direction tools do, rather than
        // reloading the whole mine model for one edge.
        const patch = toSpoolPatch(update, model.pipes);
        const recolour = patch.pipe ? { spoolColour: model.resolveSpoolBaseColour({ ...spool, pipe: patch.pipe } as PCSpool) } : {};
        model.setSpoolProperties(spool.spoolId, { ...patch, ...recolour });
        invalidate();
        onComplete();
      },
    },
  });

  const {
    values,
    errors,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
  } = formik;

  // A partly typed date is ignored until it is complete, so the field keeps what the user is typing.
  const handleDateChange = (field: 'installedOn' | 'removedOn') => (date: Dayjs | null) => {
    if (!date) {
      setFieldValue(field, null);
    } else if (date.isValid()) {
      setFieldValue(field, dayJsToLocalDate(date));
    }
  };

  return (
    <Dialog open onClose={onComplete}>
      <DialogTitle>
        <Trans id="Spool Edit">Spool Edit</Trans>
      </DialogTitle>
      <Box sx={{ width: 500, m: 2 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="body2" color="text.secondary">
            {`${i18n._('Length')}: ${metresToDisplay(spoolStraightLength(spool), unitSystem).toFixed(2)} ${unit}`}
          </Typography>

          <ProjectAuthorise permission={Permission.ManageNodeData}>
            <Box sx={{ pt: 2 }}>
              <TextField
                color="secondary"
                fullWidth
                id="name"
                name="name"
                label={i18n._('Spool Name')}
                placeholder={spoolEndsLabel(spool)}
                InputLabelProps={{ shrink: true }}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={helpers.hasError('name')}
                helperText={helpers.getErrorHelpText('name') || i18n._('Leave blank to name it after the nodes it joins')}
              />
            </Box>

            <Box sx={{ pt: 2, display: 'flex', gap: 2 }}>
              <TextField
                color="secondary"
                fullWidth
                id="level"
                name="level"
                label={i18n._('Level')}
                value={values.level}
                onChange={handleChange}
                onBlur={handleBlur}
                error={helpers.hasError('level')}
                helperText={helpers.getErrorHelpText('level')}
              />
              <TextField
                color="secondary"
                fullWidth
                id="area"
                name="area"
                label={i18n._('Area')}
                value={values.area}
                onChange={handleChange}
                onBlur={handleBlur}
                error={helpers.hasError('area')}
                helperText={helpers.getErrorHelpText('area')}
              />
            </Box>

            <Box sx={{ pt: 2 }}>
              <PipeTypeSelectField
                pipeTypes={model.pipes}
                value={values.pipeId}
                unitSystem={unitSystem}
                onChange={(pipeId) => setFieldValue('pipeId', pipeId ?? '')}
                label={i18n._('Pipe Type')}
              />
            </Box>

            <Box sx={{ pt: 2 }}>
              <FormGroup>
                <FormControlLabel
                  label={`${i18n._('Allow flow')} ${labelA} → ${labelB}`}
                  control={(
                    <Checkbox
                      color="secondary"
                      checked={values.allowFlowAB}
                      onChange={(e) => setFieldValue('allowFlowAB', e.target.checked)}
                    />
                  )}
                />
                <FormControlLabel
                  label={`${i18n._('Allow flow')} ${labelB} → ${labelA}`}
                  control={(
                    <Checkbox
                      color="secondary"
                      checked={values.allowFlowBA}
                      onChange={(e) => setFieldValue('allowFlowBA', e.target.checked)}
                    />
                  )}
                />
              </FormGroup>
              {errors.allowFlowBA && <FormHelperText error>{errors.allowFlowBA}</FormHelperText>}
            </Box>

            <Box sx={{ pt: 2 }}>
              <TextField
                color="secondary"
                fullWidth
                type="number"
                inputMode="decimal"
                id="chokeLength"
                name="chokeLength"
                label={`${i18n._('Loop Length')} (${unit})`}
                value={values.chokeLength ?? ''}
                onChange={(e) => setFieldValue('chokeLength', sanitiseNumber(e.target.value))}
                onBlur={handleBlur}
                error={helpers.hasError('chokeLength')}
                helperText={helpers.getErrorHelpText('chokeLength')}
              />
            </Box>

            <Box sx={{ pt: 2 }}>
              <TextField
                select
                color="secondary"
                fullWidth
                id="status"
                name="status"
                label={i18n._('Status')}
                value={values.status}
                onChange={(e) => setFieldValue('status', Number(e.target.value))}
                helperText={i18n._('Inactive spools are excluded from routing')}
              >
                <MenuItem value={SpoolStatus.Active}>{i18n._('Active')}</MenuItem>
                <MenuItem value={SpoolStatus.Inactive}>{i18n._('Inactive')}</MenuItem>
              </TextField>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <Box sx={{ pt: 2, display: 'flex', gap: 2 }}>
                <DatePicker
                  format="DD/MM/YYYY"
                  label={i18n._('Installed')}
                  value={values.installedOn ? LocalDateToDayJs(values.installedOn) : null}
                  onChange={handleDateChange('installedOn')}
                  slotProps={{ textField: { color: 'secondary', fullWidth: true }, field: { clearable: true } }}
                />
                <DatePicker
                  format="DD/MM/YYYY"
                  label={i18n._('Removed')}
                  value={values.removedOn ? LocalDateToDayJs(values.removedOn) : null}
                  onChange={handleDateChange('removedOn')}
                  slotProps={{
                    textField: {
                      color: 'secondary',
                      fullWidth: true,
                      error: !!errors.removedOn,
                      helperText: errors.removedOn,
                    },
                    field: { clearable: true },
                  }}
                />
              </Box>
            </LocalizationProvider>

            <Box sx={{ pt: 2 }}>
              <TextField
                color="secondary"
                fullWidth
                multiline
                minRows={2}
                id="other"
                name="other"
                label={i18n._('Other')}
                value={values.other}
                onChange={handleChange}
                onBlur={handleBlur}
                error={helpers.hasError('other')}
                helperText={helpers.getErrorHelpText('other')}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormikActions
                formik={formik}
                mutation={mutation}
                submitText={i18n._('Save')}
                right={['reset', 'submit']}
              />
            </Box>
          </ProjectAuthorise>
        </form>
      </Box>
    </Dialog>
  );
};

export default SpoolForm;
